import {
  WSButton,
  WSFlexBox,
  WSFlexGrid,
  WSForm,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  ILocationResponse,
  PhoneVerificationStatus
} from "@wingspanhq/users/dist/lib/interfaces";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { BrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { LoadingPlaceholder } from "../../components/LoadingPlaceholder";
import {
  addNotification,
  NotificationStatus
} from "../../components/Notification/Notification";
import { OnboardingLayout } from "../../components/OnboardingLayout/OnboardingLayout";
import { useUpdateActivity } from "../../query/activity";
import { useUserId } from "../../query/hooks/helpers";
import { useUserLocation } from "../../query/user";
import { useActivities, useUserProfile } from "../../query/users/queries";
import { WSQueries } from "../../query/WSQuery";
import { deleteSessionToken } from "../../services/sessionStorage";
import { trackInternationalUsers } from "../../utils/analytics";

interface OnboardingBlockFormProps {}
export const OnboardingBlockForm: React.FC<OnboardingBlockFormProps> = () => {
  const userLocationQuery = useUserLocation();
  const userId = useUserId();
  const user = useUserProfile(userId);
  const [invited, setInvited] = React.useState<boolean>(false);
  const activitiesQuery = useActivities(userId, {
    onSuccess: response => {
      setInvited(!!response.events.internationalWaitListed);
    }
  });
  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const history = useHistory();

  const onSubmit = (data: any) => {
    const userLocation = userLocationQuery.data as ILocationResponse;
    trackInternationalUsers(
      data.email,
      userLocation.isoCode,
      userLocation.countryName
    );
    updateActivity(
      {
        events: {
          internationalWaitListed: true
        }
      },
      {
        onSuccess: () => {
          setInvited(true);
          deleteSessionToken();
          addNotification({
            text: "You will be invited when Wingspan is ready in your country.",
            status: NotificationStatus.success,
            timeout: 3000
          });
        }
      }
    );
  };

  const onContinue = () => {
    if (
      !user.data?.phone?.status ||
      user.data?.phone?.status === PhoneVerificationStatus.Pending
    ) {
      history.replace("/member/verify/phone-number");
    } else {
      history.push("/member/dashboard");
    }
  };

  return (
    <>
      <BrowserPageTitle title="Getting Started" />
      <WSQueries
        queries={{ userLocationQuery, activitiesQuery }}
        renderLoader={() => <LoadingPlaceholder />}
      >
        {({ userLocationQuery: { data: userLocation } }) => (
          <OnboardingLayout>
            <WSFlexBox.Center direction="column" wrap="nowrap" mt="L">
              <WSText.Heading3>We're only in the US for now.</WSText.Heading3>
              {invited ? (
                <WSText.LabelSmall>
                  You're already on the wait list, you will be invited when
                  Wingspan is available in {userLocation.countryName}
                </WSText.LabelSmall>
              ) : (
                <WSText.LabelSmall>
                  Want us to notify you when Wingspan is available in{" "}
                  {userLocation.countryName}?
                </WSText.LabelSmall>
              )}
              {invited ? (
                <WSFlexGrid item>
                  <WSButton.Primary
                    my="S"
                    fullWidth
                    type="submit"
                    name="invite"
                    onClick={() => {
                      window.location.href = "https://wingspan.app";
                    }}
                  >
                    Back to home page
                  </WSButton.Primary>
                </WSFlexGrid>
              ) : (
                <WSForm
                  defaultValues={{ email: user.data?.email }}
                  onSubmit={onSubmit}
                >
                  <WSForm.Field name="email" mt="L" mb="S">
                    <WSForm.Label>Email address</WSForm.Label>
                    <WSForm.Input
                      render={props => <WSTextInput type="email" {...props} />}
                    />
                    <WSForm.Error />
                  </WSForm.Field>

                  <WSButton.Primary
                    mb="S"
                    fullWidth
                    type="submit"
                    name="invite"
                    loading={updateActivityMeta.isLoading}
                  >
                    Invite me to Wingspan later
                  </WSButton.Primary>
                  <WSButton.Secondary
                    fullWidth
                    name="continueOnboarding"
                    onClick={onContinue}
                  >
                    I'm US-based, temporarily abroad
                  </WSButton.Secondary>
                </WSForm>
              )}
            </WSFlexBox.Center>
          </OnboardingLayout>
        )}
      </WSQueries>
    </>
  );
};
